import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { RollBlank } from "../models/RollBlank";
import { MillSizeSelector } from "./MillSizeSelector";
import { RollApplicationSelector } from "./RollApplicationSelector";
import { RollMaterialSelector } from "./RollMaterialSelector";
import { RollBlankAttachments } from "./RollBlankAttachments";

interface FormErrors {
	Dwg?: string;
}

interface RollBlankDetailViewerProps {
	blank: RollBlank | null;
	existingBlanks: RollBlank[];
	isSaving: boolean;
	onSave: (blank: RollBlank) => void;
	onClose: () => void;
}

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

export const RollBlankDetailViewer = ({
	blank,
	existingBlanks,
	isSaving,
	onSave,
	onClose,
}: RollBlankDetailViewerProps) => {
	const [formData, setFormData] = useState<RollBlank | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewBlank = !blank?.Id;

	const validateForm = useMemo(() => {
		return (data: RollBlank | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Dwg || data.Dwg.trim().length === 0) {
				newErrors.Dwg = "Dwg is required";
			} else {
				const isDuplicate = existingBlanks.some(
					(b) =>
						b.Id !== data.Id &&
						b.Dwg.toLowerCase() === data.Dwg.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Dwg = "This Dwg already exists";
				}
			}

			return newErrors;
		};
	}, [existingBlanks]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewBlank && isFormValid;

	useEffect(() => {
		if (blank) {
			setFormData(sanitizeNullStrings({ ...blank }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [blank]);

	const handleChange = (field: keyof RollBlank, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof RollBlank) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof RollBlank, value: unknown) => {
		handleBlur(field);

		if (isNewBlank) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = blank?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof RollBlank, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewBlank) return;

		if (blank?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (field: keyof RollBlank, rawValue: string) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Dwg: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!blank) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a roll blank to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewBlank ? "New Roll Blank" : formData.Dwg}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewBlank && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				{/* Basic Information */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Basic Information</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Dwg"
						value={formData.Dwg}
						onChange={(e) => handleChange("Dwg", e.target.value)}
						onBlur={(e) => handleBlurSave("Dwg", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Dwg && !!errors.Dwg}
						helperText={touched.Dwg && errors.Dwg}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<MillSizeSelector
						value={formData.MillSizeId}
						onChange={(value) => handleSelectChange("MillSizeId", value)}
						fullWidth
						size="small"
					/>
				</Grid2>

				{/* Active */}
				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) => handleSelectChange("Active", e.target.checked)}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>

				{/* Dimensions */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Dimensions</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 4 }}>
					<TextField
						label="Width"
						type="number"
						value={formData.Width ?? ""}
						onChange={(e) =>
							handleChange(
								"Width",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("Width", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 4 }}>
					<TextField
						label="New Diameter"
						type="number"
						value={formData.NewDiameter ?? ""}
						onChange={(e) =>
							handleChange(
								"NewDiameter",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("NewDiameter", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 4 }}>
					<TextField
						label="Scrap Diameter"
						type="number"
						value={formData.ScrapDiameter ?? ""}
						onChange={(e) =>
							handleChange(
								"ScrapDiameter",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) =>
							handleNumericBlurSave("ScrapDiameter", e.target.value)
						}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>

				{/* Hardness */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Hardness</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Hardness Minimum"
						type="number"
						value={formData.HardnessMinimum ?? ""}
						onChange={(e) =>
							handleChange(
								"HardnessMinimum",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) =>
							handleNumericBlurSave("HardnessMinimum", e.target.value)
						}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Hardness Maximum"
						type="number"
						value={formData.HardnessMaximum ?? ""}
						onChange={(e) =>
							handleChange(
								"HardnessMaximum",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) =>
							handleNumericBlurSave("HardnessMaximum", e.target.value)
						}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>

				{/* Costs & Tolerances */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Costs & Tolerances</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Est R Cost"
						type="number"
						value={formData.EstRCost ?? ""}
						onChange={(e) =>
							handleChange(
								"EstRCost",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("EstRCost", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Est S Cost"
						type="number"
						value={formData.EstSCost ?? ""}
						onChange={(e) =>
							handleChange(
								"EstSCost",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("EstSCost", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="D Tol"
						type="number"
						value={formData.DTol ?? ""}
						onChange={(e) =>
							handleChange(
								"DTol",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("DTol", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="D Tolm"
						type="number"
						value={formData.DTolm ?? ""}
						onChange={(e) =>
							handleChange(
								"DTolm",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("DTolm", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.001 },
						}}
					/>
				</Grid2>

				{/* References */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>References</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<RollApplicationSelector
						value={formData.RollApplicationId}
						onChange={(value) => handleSelectChange("RollApplicationId", value)}
						fullWidth
						size="small"
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<RollMaterialSelector
						value={formData.RollMaterialId}
						onChange={(value) => handleSelectChange("RollMaterialId", value)}
						fullWidth
						size="small"
					/>
				</Grid2>

				{/* Additional */}
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Additional</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) =>
							handleChange("Description", e.target.value || null)
						}
						onBlur={(e) =>
							handleBlurSave("Description", e.target.value || null)
						}
						fullWidth
						size="small"
						multiline
						rows={3}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="PIT Program Name"
						value={formData.PitProgramName ?? ""}
						onChange={(e) =>
							handleChange("PitProgramName", e.target.value || null)
						}
						onBlur={(e) =>
							handleBlurSave("PitProgramName", e.target.value || null)
						}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{/* Attachments */}
				{!isNewBlank && (
					<>
						<Grid2 size={{ xs: 12 }}>
							<Typography sx={sectionHeaderSx}>Attachments</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<RollBlankAttachments
								rollBlankId={formData.Id}
								disabled={isSaving}
							/>
						</Grid2>
					</>
				)}

				{/* DWG File Info (read-only for legacy data) */}
				{formData.DwgName && (
					<>
						<Grid2 size={{ xs: 12 }}>
							<Typography sx={sectionHeaderSx}>DWG File (Legacy)</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								label="DWG File Name"
								value={formData.DwgName ?? ""}
								fullWidth
								size="small"
								disabled
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						</Grid2>
					</>
				)}
			</Grid2>
		</Box>
	);
};
